import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';

const LOCK_CLASSES = ['overflow-hidden', 'fixed', 'w-full'];
const LOCKED_TEST_CLASS = 'body-locked';

/** Each overlay locks with its own id; the body stays locked while any id is active. */
@Injectable({ providedIn: 'root' })
export class BodyLockService {
	private readonly document = inject(DOCUMENT);
	private activeLockIds: ReadonlySet<symbol> = new Set();
	private scrollPosition = 0;

	lock(lockId: symbol): void {
		if (this.activeLockIds.has(lockId)) return;

		if (this.activeLockIds.size === 0) {
			this.scrollPosition = this.document.defaultView?.scrollY ?? 0;
			this.document.body.classList.add(...LOCK_CLASSES);
			this.document.body.style.top = `-${this.scrollPosition}px`;
			this.document.body.dataset['testClass'] = LOCKED_TEST_CLASS;
		}
		this.activeLockIds = new Set([...this.activeLockIds, lockId]);
	}

	unlock(lockId: symbol, restoreScroll = true): void {
		if (!this.activeLockIds.has(lockId)) return;

		this.activeLockIds = new Set(
			[...this.activeLockIds].filter((current) => current !== lockId),
		);
		if (this.activeLockIds.size > 0) return;

		this.document.body.classList.remove(...LOCK_CLASSES);
		this.document.body.style.removeProperty('top');
		delete this.document.body.dataset['testClass'];
		if (restoreScroll) {
			this.document.defaultView?.scrollTo(0, this.scrollPosition);
		}
	}
}

export function injectBodyLock() {
	const service = inject(BodyLockService);
	const lockId = Symbol('body-lock');

	let destroyed = false;
	inject(DestroyRef).onDestroy(() => {
		destroyed = true;
		service.unlock(lockId, false);
	});

	// A host can schedule a lock (e.g. in a timeout) that only lands after it is destroyed
	const lock = () => !destroyed && service.lock(lockId);
	const unlock = () => service.unlock(lockId);

	return {
		lock,
		unlock,
		toggle: (locked: boolean) => (locked ? lock() : unlock()),
	};
}
